import { siteMeta } from "@/content/siteContent";

const siteUrl = process.env.REACT_APP_BACKEND_URL;
const defaultImage = siteMeta.heroImage;

const getAbsoluteUrl = (path = "/") => {
  const normalizedPath = path === "/" ? "" : path;
  return `${siteUrl}${normalizedPath}`;
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "Concept Magik",
  url: getAbsoluteUrl("/"),
  image: defaultImage,
  logo: siteMeta.logoImage,
  description:
    "Food service design consultants, food service business consultants, and commercial kitchen design consultants serving hospitality projects across the USA.",
  telephone: siteMeta.phone,
  email: siteMeta.email,
  areaServed: {
    "@type": "Country",
    name: "United States",
  },
  serviceType: [
    "Food service design consulting",
    "Food service business consulting",
    "Commercial kitchen design consulting",
  ],
};

const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: getAbsoluteUrl(item.path),
  })),
});

export const defaultSeo = {
  title: "Food Service Design & Business Consultants USA",
  description:
    "Food service design consultants, food service business consultants, and commercial kitchen design consultants serving restaurants, hotels, caterers, and hospitality projects across the USA.",
  keywords:
    "food service design consultants, food service business consultants, commercial kitchen design consultants, hospitality consultants USA, restaurant design consultants, foodservice consultants",
  image: defaultImage,
  path: "/",
  schemas: [
    organizationSchema,
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Concept Magik",
      url: getAbsoluteUrl("/"),
      description:
        "Food service design and business consulting services for hospitality projects across the USA.",
    },
    breadcrumbSchema([{ name: "Home", path: "/" }]),
  ],
};

export const seoByPath = {
  "/": defaultSeo,
  "/services": {
    title: "Food Service Consulting Services USA",
    description:
      "Explore Concept Magik services for concept development, feasibility strategy, design process, and business advisory support for food service and commercial kitchen projects in the USA.",
    keywords:
      "food service consulting services, commercial kitchen design consulting, concept development hospitality, feasibility strategy foodservice, business advisory food service",
    image: defaultImage,
    path: "/services",
    schemas: [
      organizationSchema,
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Concept Magik Services",
        url: getAbsoluteUrl("/services"),
        description:
          "Concept development, feasibility, design process, business advisory, and additional food service consulting services.",
      },
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
      ]),
    ],
  },
  "/clients": {
    title: "Client Portfolio | Food Service Consulting Projects",
    description:
      "Explore Concept Magik's client portfolio across food service consulting, hospitality design, institutional dining, corporate facilities, and religious establishment projects in the USA and internationally.",
    keywords:
      "Concept Magik clients, food service consulting projects, hospitality design portfolio, institutional dining consultants, commercial kitchen consulting clients, client showcase",
    image: defaultImage,
    path: "/clients",
    schemas: [
      organizationSchema,
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Concept Magik Clients",
        url: getAbsoluteUrl("/clients"),
        description:
          "Client portfolio spanning food service consulting, hospitality design, institutional dining, corporate, and religious establishment work.",
      },
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Clients", path: "/clients" },
      ]),
    ],
  },
  "/industries": {
    title: "Food Service Industry Consulting USA",
    description:
      "Food service consultants supporting restaurants, cafés, hotels, banquet operations, institutional kitchens, distributors, and specialty hospitality venues across the USA.",
    keywords:
      "food service industry consulting, restaurant consultants USA, hotel foodservice consulting, institutional kitchen consultants, hospitality consulting USA",
    image: defaultImage,
    path: "/industries",
    schemas: [
      organizationSchema,
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Industries Served by Concept Magik",
        url: getAbsoluteUrl("/industries"),
        description:
          "Industry-specific consulting support for hospitality and food service operations in the USA.",
      },
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Industries", path: "/industries" },
      ]),
    ],
  },
  "/who-we-are": {
    title: "Food Service Consulting Team USA",
    description:
      "Meet the Concept Magik leadership team with global experience in food service design, hospitality operations, consulting, culinary strategy, and commercial kitchen planning.",
    keywords:
      "food service consulting team, hospitality consultants leadership, commercial kitchen consultants team, Concept Magik team, foodservice experts USA",
    image: defaultImage,
    path: "/who-we-are",
    schemas: [
      organizationSchema,
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "Who We Are | Concept Magik",
        url: getAbsoluteUrl("/who-we-are"),
        description:
          "Leadership profiles and consulting experience behind Concept Magik's food service design and business consulting practice.",
      },
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Who We Are", path: "/who-we-are" },
      ]),
    ],
  },
  "/about": {
    title: "Food Service Consulting Team USA",
    description:
      "Meet the Concept Magik leadership team with global experience in food service design, hospitality operations, consulting, culinary strategy, and commercial kitchen planning.",
    keywords:
      "food service consulting team, hospitality consultants leadership, commercial kitchen consultants team, Concept Magik team, foodservice experts USA",
    image: defaultImage,
    path: "/about",
    schemas: [
      organizationSchema,
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "About", path: "/about" },
      ]),
    ],
  },
  "/contact": {
    title: "Contact Food Service Consultants USA",
    description:
      "Contact Concept Magik, food service design and business consultants in the USA. Reach Vijay K. Oza and project contacts for hospitality consulting and commercial kitchen design enquiries.",
    keywords:
      "contact food service consultants, contact commercial kitchen design consultants, hospitality consultants USA contact, Concept Magik contact",
    image: defaultImage,
    path: "/contact",
    schemas: [
      organizationSchema,
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "Contact Concept Magik",
        url: getAbsoluteUrl("/contact"),
        description:
          "Contact details for Concept Magik food service design and business consultants in the USA.",
      },
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Contact", path: "/contact" },
      ]),
    ],
  },
};

export const getSeoForPath = (pathname) => seoByPath[pathname] ?? defaultSeo;
